import React, {useState} from 'react';
import _ from 'underscore';

import * as Analytics from 'analytics/Analytics';
import {AnalyticsMetadataForContext} from 'analytics/AnalyticsTypes';
import {ActionOrigin, ANALYTICS_MODULE, COMMON_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import FolderSelector from 'file-browser/FolderSelector';
import {InlineBannerProps, InlineBannerType} from 'ui-library/banners/InlineBanner';
import InlineBannerStack from 'ui-library/banners/InlineBannerStack';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonStyle} from 'ui-library/buttons/Button';
import useOnMount from 'ui-library/hooks/useOnMount';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';
import {withLoadingScreen} from 'util/Promises';

import {cloneAnalysis} from './AnalysisActions';
import {AnalysisGraphQL, isAnalysisOutputStepType} from './AnalysisTypes';

/** @package */
export interface CloneAnalysisModalProps {
  analysisToClone: AnalysisGraphQL;
  onCloneAnalysis: typeof cloneAnalysis;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

const ANALYTICS_METADATA: AnalyticsMetadataForContext = {
  actionOrigin: ActionOrigin.ANALYSIS_TOOL,
  modalType: 'clone_analysis',
};

/** @package */
export const CLONE_ANALYSIS_OUTPUTS_WARNING =
  'This analysis has generated outputs that will not be duplicated.';

function getCloneAnalysisWarnings(analysisToClone: AnalysisGraphQL): Array<InlineBannerProps> {
  return _.compact([
    analysisToClone.execution.steps.some((step) => isAnalysisOutputStepType(step.definition.stepType))
      ? {
          bannerType: InlineBannerType.WARNING,
          text: CLONE_ANALYSIS_OUTPUTS_WARNING,
        }
      : null,
  ]);
}

/** @package */
export function CloneAnalysisModal({
  analysisToClone,
  onCloneAnalysis,
  onRequestClose,
  onRequestSubmit,
}: CloneAnalysisModalProps): JSX.Element {
  const [folderId, setFolderId] = useState<string | null>(analysisToClone.folder.id);

  const warnings = getCloneAnalysisWarnings(analysisToClone);

  async function handleRequestSubmit(): Promise<void> {
    await withLoadingScreen(
      onCloneAnalysis(analysisToClone.id, {folderId: folderId!}),
      'Duplicating analysis...'
    );
    onRequestSubmit();
  }

  useOnMount(() => {
    Analytics.trackEventWithModule(
      COMMON_ANALYTICS_ACTIONS.OPENED_MODAL,
      ANALYTICS_MODULE.ANALYSIS_TOOL,
      ANALYTICS_METADATA
    );
  });

  return (
    <Modal
      title="Duplicate analysis"
      onRequestSubmit={() => {
        Analytics.trackEventWithModule(
          COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL,
          ANALYTICS_MODULE.ANALYSIS_TOOL,
          ANALYTICS_METADATA
        );
        handleRequestSubmit();
      }}
      onRequestClose={() => {
        Analytics.trackEventWithModule(
          COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL,
          ANALYTICS_MODULE.ANALYSIS_TOOL,
          ANALYTICS_METADATA
        );
        onRequestClose();
      }}
      buttons={[
        {
          submit: true,
          text: 'Duplicate',
          style: ButtonStyle.SECONDARY,
        },
      ]}
    >
      <FormGroup>
        <>
          <FieldLabel label="Folder" />
          <FolderSelector
            enableRedesign={true}
            showNotebook={true}
            value={folderId}
            onChange={(e: {target: {value: string}}) => {
              setFolderId(e.target.value);
            }}
            unsetOnFetchFail={true}
          />
        </>
      </FormGroup>
      {warnings.length > 0 && <InlineBannerStack messages={warnings} />}
    </Modal>
  );
}

export default function ConnectedCloneAnalysisModal(
  props: Omit<CloneAnalysisModalProps, 'onCloneAnalysis'>
): JSX.Element {
  return <CloneAnalysisModal {...props} onCloneAnalysis={cloneAnalysis} />;
}
